import { PhoneInput } from '@/components/ui/phone-input';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { AlertCircle } from 'lucide-react';
import { useIntl } from 'react-intl';
import { type Country } from 'react-phone-number-input';

import { roleStepMessages } from './role-step.messages';

interface PhoneNumberFieldProps {
  isOptional: boolean;
  phoneNumber: string;
  onChange: (value: string) => void;
  allowedCountries: Country[];
  defaultCountry?: Country;
  hasCountryPhoneMismatch: boolean;
  inputClassName?: string;
}

/**
 * Phone number field for the onboarding steps: required/optional label,
 * the phone input, and the country/number mismatch warning.
 */
export function PhoneNumberField({
  isOptional,
  phoneNumber,
  onChange,
  allowedCountries,
  defaultCountry,
  hasCountryPhoneMismatch,
  inputClassName,
}: PhoneNumberFieldProps) {
  const intl = useIntl();

  return (
    <>
      {isOptional ? (
        <TypographyP2Bold className="leading-tight">
          {intl.formatMessage(roleStepMessages.phoneNumberOptional)}
        </TypographyP2Bold>
      ) : (
        <div className="flex gap-0.5">
          <TypographyP2Bold className="leading-tight">
            {intl.formatMessage(roleStepMessages.phoneNumber)}
          </TypographyP2Bold>
          <TypographyP2Bold className="text-validation-destructive">*</TypographyP2Bold>
        </div>
      )}
      <PhoneInput
        value={phoneNumber}
        onChange={(value) => onChange((value as string) ?? '')}
        allowedCountries={allowedCountries}
        defaultCountry={defaultCountry}
        placeholder={intl.formatMessage(roleStepMessages.phoneNumberPlaceholder)}
        className={inputClassName}
      />
      {hasCountryPhoneMismatch && (
        <div className="flex items-start gap-2 rounded-md bg-validation-warning-muted p-2">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-validation-warning" />
          <TypographyCaption className="text-validation-warning">
            {intl.formatMessage(roleStepMessages.countryPhoneMismatchWarning)}
          </TypographyCaption>
        </div>
      )}
    </>
  );
}
